import { ReactElement } from 'react';
import { WithCount } from '@client/catalog/components/WithCount';
import { useTranslate } from '@client/i18n/hooks';
import { ROUTES } from '@client/routes/components/Router/routes';
import { getCategoryUrlParams } from '@client/routes/utils/url';
import { ActionLink } from '@lib/components/ActionLink';
import { Icon, ICON_NAME, ICON_SIZE } from '@lib/components/Icon';
import { Props } from './Categories.types';
import './Categories.scss';

const Categories = ({
  categories,
  getCurrentCategory,
  getParentCategory,
  goToCategory,
  isOptionChecked,
  urlCreator,
}: Props): ReactElement => {
  const t = useTranslate();

  const { available, suggested } = categories;
  const parentCategory = getParentCategory();
  const currentCategory = getCurrentCategory();
  const areSuggestedCategoriesSpecified = !!suggested.length;

  const renderCategories = (): ReactElement[] => available.map(({ name: label, field: value, count }) => (
    <a
      aria-checked={isOptionChecked(value)}
      className={currentCategory ? 'Categories-Item' : 'Categories-MainItem'}
      href={`${urlCreator(ROUTES.category)}${getCategoryUrlParams(value)}`}
      key={value}
      onClick={(e) => goToCategory(e, value, label)}
      role='checkbox'
    >
      <WithCount count={count}>
        <Icon name={ICON_NAME.arrowRight} size={ICON_SIZE.xsmall} />

        <span className='Categories-ItemLabel'>{label}</span>
      </WithCount>
    </a>
  ));

  const renderSuggestedCategories = (): ReactElement | null => {
    if (areSuggestedCategoriesSpecified) {
      return (
        <div className='Catalog-FiltersGroup'>
          <div className='Catalog-FiltersHeading Catalog-FiltersHeading--Small'>
            {t('Suggested categories').toUpperCase()}
          </div>

          {suggested.map(({ name: label, field: value, count }) => (
            <a
              className='Categories-ItemSimple'
              href={`${urlCreator(ROUTES.category)}${getCategoryUrlParams(value)}`}
              key={value}
              onClick={(e) => goToCategory(e, value, label)}
            >
              <WithCount count={count}>
                <span className='Categories-ItemLabel'>{label}</span>
              </WithCount>
            </a>
          ))}
        </div>
      );
    }

    return null;
  };

  const renderParentCategory = (): ReactElement | null => parentCategory && (
    <ActionLink
      iconName={ICON_NAME.arrowLeft}
      iconSize={ICON_SIZE.xsmall}
      href={`${urlCreator(ROUTES.category)}${getCategoryUrlParams(parentCategory.field)}`}
      onClick={(e) => goToCategory(e, parentCategory.field, parentCategory.name)}
    >
      {parentCategory.name}
    </ActionLink>
  );

  const renderCurrentCategory = (): ReactElement | null => currentCategory && (
    <div className='Categories-Current'>
      <div className='WithCount-Label'>{currentCategory.name}</div>
    </div>
  );

  return (
    <form className='Categories'>
      <div className='Catalog-FiltersHeading'>{t('Categories')}</div>

      {renderSuggestedCategories()}

      <div className='Catalog-FiltersGroup'>
        {areSuggestedCategoriesSpecified && (
          <div className='Catalog-FiltersHeading Catalog-FiltersHeading--Small'>
            {t('All categories').toUpperCase()}
          </div>
        )}

        {renderParentCategory()}
        {renderCurrentCategory()}
        {renderCategories()}
      </div>
    </form>
  );
};

export { Categories };
